import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { AISummary } from "../components/AISummary";
import { 
  Search, 
  SlidersHorizontal, 
  ChevronDown,
  Pencil,
  X,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Plus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Groceries", "Dining", "Transport", "Travel", "Utilities", "Entertainment",
  "Shopping", "Health", "Personal", "Income", "Transfer", "Subscriptions", 
  "Housing", "Debt Payment", "Bank Fees", "Other"
];

const CATEGORY_COLORS = {
  Groceries: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  Dining: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  Transport: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  Travel: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  Utilities: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  Entertainment: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  Shopping: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  Health: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  Personal: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  Income: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  Transfer: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  Subscriptions: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  Housing: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  "Debt Payment": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "Bank Fees": "bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-400",
  Other: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
};

export const TransactionsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editCategory, setEditCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Add transaction modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    merchant: "",
    category: "Other",
    type: "expense" // expense or income
  });
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [txnRes, accRes] = await Promise.all([
        api.get("/transactions", { params: { limit: 100 } }),
        api.get("/accounts")
      ]);
      setTransactions(txnRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchFilteredTransactions = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (search) params.search = search;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (accountFilter !== "all") params.account_id = accountFilter;
      
      const response = await api.get("/transactions", { params });
      setTransactions(response.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  }, [search, categoryFilter, accountFilter, api]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchFilteredTransactions();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, categoryFilter, accountFilter, fetchFilteredTransactions]);

  const handleUpdateCategory = async () => {
    if (!selectedTransaction || !editCategory) return;
    
    setSaving(true);
    try {
      await api.patch(`/transactions/${selectedTransaction.id}`, {
        category_override: editCategory
      });
      
      // Update all transactions with the same merchant (smart category)
      const merchantName = selectedTransaction.merchant;
      let updatedCount = 0;
      
      setTransactions(prev => prev.map(t => {
        if (t.merchant === merchantName) {
          updatedCount++;
          return { ...t, category_override: editCategory };
        }
        return t;
      }));
      
      if (updatedCount > 1) {
        toast.success(`Category updated for ${updatedCount} "${merchantName}" transactions`);
      } else {
        toast.success("Category updated");
      }
      setSelectedTransaction(null);
    } catch (error) {
      toast.error("Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!newTransaction.merchant || !newTransaction.amount) {
      toast.error("Please fill in merchant and amount");
      return;
    }

    setAdding(true);
    try {
      const amount = newTransaction.type === "expense" 
        ? -Math.abs(parseFloat(newTransaction.amount))
        : Math.abs(parseFloat(newTransaction.amount));

      await api.post("/transactions/manual", {
        date: newTransaction.date,
        amount: amount,
        merchant: newTransaction.merchant,
        category: newTransaction.category
      });

      toast.success("Transaction added");
      setShowAddModal(false);
      setNewTransaction({
        date: new Date().toISOString().split("T")[0],
        amount: "",
        merchant: "",
        category: "Other",
        type: "expense"
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add transaction");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteTransaction = async (txnId) => {
    setDeleting(txnId);
    try {
      await api.delete(`/transactions/${txnId}`);
      toast.success("Transaction deleted");
      setTransactions(transactions.filter(t => t.id !== txnId));
    } catch (error) {
      if (error.response?.data?.detail?.includes("manual")) {
        toast.error("Only manual transactions can be deleted");
      } else {
        toast.error("Failed to delete transaction");
      }
    } finally {
      setDeleting(null);
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(Math.abs(amount));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    
    return date.toLocaleDateString("en-CA", { 
      weekday: "short",
      month: "short", 
      day: "numeric",
    });
  };

  const getDisplayCategory = (txn) => txn.category_override || txn.category;

  const groupTransactionsByDate = (txns) => {
    const groups = {};
    txns.forEach(txn => {
      const date = txn.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(txn);
    });
    return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  const hasActiveFilters = categoryFilter !== "all" || accountFilter !== "all";

  if (loading) {
    return (
      <div className="space-y-6" data-testid="transactions-loading">
        <div>
          <Skeleton className="h-8 w-40 mb-2" />
          <Skeleton className="h-5 w-56" />
        </div>
        <Skeleton className="h-12 w-full rounded-xl" />
        <div className="space-y-4">
          {[1,2,3,4,5].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const groupedTransactions = groupTransactionsByDate(transactions);



  var CAT_DOTS = {"Groceries":"#3F7D52","Dining":"#B0603F","Transport":"#7ED957","Housing":"#14432B","Income":"#2f6e46","Other":"#C68A3C","Shopping":"#C68A3C","Utilities":"#C68A3C","Entertainment":"#8b5cf6","Subscriptions":"#3b82f6","default":"#C68A3C"};
  var catDot = function(cat) { return CAT_DOTS[cat] || CAT_DOTS["default"]; };

  // Compute spending summary
  var totalSpending = transactions.filter(function(t){return t.amount < 0;}).reduce(function(s,t){return s + Math.abs(t.amount);}, 0);
  var totalIncome = transactions.filter(function(t){return t.amount > 0;}).reduce(function(s,t){return s + t.amount;}, 0);
  var netAmount = totalIncome - totalSpending;

  // Category breakdown for segmented bar
  var catTotals = {};
  transactions.filter(function(t){return t.amount < 0;}).forEach(function(t){
    var c = t.category_override || t.category || "Other";
    catTotals[c] = (catTotals[c] || 0) + Math.abs(t.amount);
  });
  var catEntries = Object.entries(catTotals).sort(function(a,b){return b[1]-a[1];});
  var topCats = catEntries.slice(0, 4);
  var restAmt = catEntries.slice(4).reduce(function(s,e){return s + e[1];}, 0);
  if (restAmt > 0) topCats.push(["Everything else", restAmt]);
  var segColors = ["#C68A3C","#14432B","#3F7D52","#B0603F","#9CCB86"];

  // Unique categories for filter chips
  var uniqueCats = ["All"];
  transactions.forEach(function(t) {
    var c = t.category_override || t.category || "Other";
    if (uniqueCats.indexOf(c) === -1) uniqueCats.push(c);
  });

  return (
    <div data-testid="transactions-page">
      {/* Header */}
      <div className="phead">
        <div><h1>Activity</h1><div className="sub">{transactions.length} transactions this month</div></div>
        <div className="actions"><button className="btn btn-p" onClick={function(){setShowAddModal(true);}} data-testid="add-transaction-btn"><Plus size={16} stroke="#F4EFE4" /> Add transaction</button></div>
      </div>

      {/* Spending Summary Card */}
      <div className="card" style={{padding:"26px 30px",marginBottom:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:40,flexWrap:"wrap"}}>
          <div style={{flex:"none"}}>
            <span className="lab">Spent this month</span>
            <div className="serif" style={{fontSize:40,fontWeight:600,letterSpacing:"-1.2px",margin:"8px 0 4px"}}>{formatCurrency(totalSpending)}</div>
            <div className="muted" style={{fontSize:14}}>{formatCurrency(totalIncome)} in · <span style={{color: netAmount >= 0 ? "#3F7D52" : "#B0603F",fontWeight:600}}>{netAmount >= 0 ? "+" : "−"}{formatCurrency(Math.abs(netAmount))} net</span></div>
          </div>
          {topCats.length > 0 && <div style={{flex:1,minWidth:300}}>
            <span className="lab" style={{display:"block",marginBottom:12}}>Where it went</span>
            <div className="segbar" style={{display:"flex",height:12,borderRadius:12,overflow:"hidden",gap:2,marginBottom:12}}>
              {topCats.map(function(c,i){var pct = totalSpending > 0 ? (c[1]/totalSpending*100) : 0; return <div key={i} style={{width:pct+"%",background:segColors[i]||"#9CCB86",borderRadius:12}} />;})}
            </div>
            <div className="legend" style={{display:"flex",flexWrap:"wrap",gap:14}}>
              {topCats.map(function(c,i){return <div key={i} className="lg" style={{display:"flex",alignItems:"center",gap:6,fontSize:13}}>
                <span className="lgdot" style={{width:8,height:8,borderRadius:"50%",background:segColors[i]||"#9CCB86",display:"inline-block"}} />
                <span>{c[0]}</span><b style={{fontWeight:600,marginLeft:2}}>{formatCurrency(c[1])}</b>
              </div>;})}
            </div>
          </div>}
        </div>
      </div>

      {/* AI Insight */}
      <AISummary page="transactions" />

      {/* Filter Chips */}
      <div className="chiprow" style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {uniqueCats.slice(0,6).map(function(c){
          var isOn = (c === "All" && categoryFilter === "all") || c === categoryFilter;
          return <button key={c} className={"fc" + (isOn ? " on" : "")} style={{height:34,padding:"0 16px",borderRadius:50,border: isOn ? "none" : "1.5px solid #ECE8DC",background: isOn ? "#14432B" : "#fff",color: isOn ? "#F4EFE4" : "#7d7d70",fontSize:"13.5px",fontWeight:500,cursor:"pointer",fontFamily:"inherit"}} onClick={function(){setCategoryFilter(c === "All" ? "all" : c);}}>{c}</button>;
        })}
      </div>

      {/* Search + Filters */}
      <div style={{display:"flex",gap:12,marginBottom:6}}>
        <div className="searchbar" style={{display:"flex",alignItems:"center",gap:10,height:44,padding:"0 16px",border:"1.5px solid #ECE8DC",borderRadius:14,background:"#fff",flex:1}}>
          <Search size={18} stroke="#a6a698" />
          <input placeholder="Search transactions..." value={search} onChange={function(e){setSearch(e.target.value);}} style={{border:"none",outline:"none",background:"transparent",fontSize:"14.5px",color:"#23241E",fontFamily:"inherit",width:"100%"}} data-testid="search-input" />
        </div>
        <button className="btn btn-g" style={{display:"inline-flex",alignItems:"center",gap:7,height:44,padding:"0 20px",borderRadius:50,border:"1.5px solid #ECE8DC",background:"#fff",color:"#14432B",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}} onClick={function(){setShowFilters(!showFilters);}}>
          <SlidersHorizontal size={16} stroke="#14432B" /> Filters
        </button>
      </div>

      {/* Transaction List */}
      {groupedTransactions.map(function(group) {
        var date = group[0]; var txns = group[1];
        return (
          <div key={date} className="day">
            <div className="daylabel" style={{fontSize:13,fontWeight:600,color:"#7d7d70",padding:"12px 0 8px",textTransform:"uppercase",letterSpacing:".04em"}}>{formatDate(date)}</div>
            <div className="card">
              {txns.map(function(txn) {
                var cat = getDisplayCategory(txn);
                var isIncome = txn.amount > 0;
                return (
                  <div key={txn.id} className="txn" style={{display:"flex",alignItems:"center",gap:14,padding:"14px 20px",borderBottom:"1px solid #ECE8DC",cursor:"pointer"}} onClick={function(){setSelectedTransaction(txn);setEditCategory(cat);}}>
                    <div className={"ti" + (isIncome ? " inc" : "")} style={{width:36,height:36,borderRadius:10,background: isIncome ? "rgba(63,125,82,.08)" : "#F4EFE4",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {isIncome ? <ArrowDownRight size={17} stroke="#3F7D52" /> : <ArrowUpRight size={17} stroke="#8a8a7c" />}
                    </div>
                    <div className="nm" style={{flex:1,minWidth:0}}>
                      <b style={{display:"block",fontSize:"14.5px",fontWeight:600}}>{txn.merchant}</b>
                      <div style={{display:"flex",gap:6,marginTop:3}}>
                        <span className="pill tag" style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,fontWeight:500,padding:"2px 10px",borderRadius:50,background:"#F4EFE4",color:"#7d7d70"}}>
                          <span className="pd" style={{width:7,height:7,borderRadius:"50%",background:catDot(cat),display:"inline-block"}} />{cat}
                        </span>
                        {txn.pending && <span className="pill sand" style={{fontSize:12,padding:"2px 10px",borderRadius:50,background:"#E7DCC7",color:"#7d7d70",fontWeight:500}}>Pending</span>}
                      </div>
                    </div>
                    <div className="amt" style={{fontSize:15,fontWeight:600,whiteSpace:"nowrap",color: isIncome ? "#3F7D52" : "inherit"}}>{isIncome ? "+" : "−"}{formatCurrency(txn.amount)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {transactions.length === 0 && <div className="card" style={{padding:72,textAlign:"center"}}><div className="empty" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}><div style={{width:56,height:56,borderRadius:16,background:"#DCEAD8",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}><ArrowLeftRight size={26} stroke="#14432B" /></div><div className="serif" style={{fontSize:23,fontWeight:600,color:"#23241E",marginBottom:6}}>No transactions yet</div><div style={{maxWidth:360,color:"#7d7d70"}}>Connect your bank to see your transactions here.</div></div></div>}

      {/* Edit Category Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={function(o){if(!o)setSelectedTransaction(null);}}>
        <DialogContent style={{background:"#fff",borderColor:"#ECE8DC",borderRadius:20,fontFamily:"'Hanken Grotesk',sans-serif"}}>
          <DialogHeader><DialogTitle style={{fontFamily:"'Lora',Georgia,serif"}}>Edit Category</DialogTitle><DialogDescription>Update the category for "{selectedTransaction?.merchant}"</DialogDescription></DialogHeader>
          <div style={{marginBottom:16}}><Label>Category</Label>
            <Select value={editCategory} onValueChange={setEditCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(function(c){return <SelectItem key={c} value={c}>{c}</SelectItem>;})}</SelectContent></Select>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button className="btn btn-g" onClick={function(){setSelectedTransaction(null);}}>Cancel</button>
            <button className="btn btn-p" onClick={handleUpdateCategory} disabled={saving}>{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save</button>
          </div>
          {selectedTransaction?.is_manual && <button style={{marginTop:12,background:"none",border:"none",color:"#B0603F",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}} onClick={function(){handleDeleteTransaction(selectedTransaction.id);setSelectedTransaction(null);}}>Delete transaction</button>}
        </DialogContent>
      </Dialog>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent style={{background:"#fff",borderColor:"#ECE8DC",borderRadius:20,fontFamily:"'Hanken Grotesk',sans-serif"}}>
          <DialogHeader><DialogTitle style={{fontFamily:"'Lora',Georgia,serif"}}>Add Transaction</DialogTitle></DialogHeader>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><Label>Merchant</Label><input style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} placeholder="e.g. Starbucks" value={newTransaction.merchant} onChange={function(e){setNewTransaction(function(p){return{...p,merchant:e.target.value};});}} /></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div><Label>Amount ($)</Label><input type="number" style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} placeholder="0.00" value={newTransaction.amount} onChange={function(e){setNewTransaction(function(p){return{...p,amount:e.target.value};});}} /></div>
              <div><Label>Date</Label><input type="date" style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} value={newTransaction.date} onChange={function(e){setNewTransaction(function(p){return{...p,date:e.target.value};});}} /></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div><Label>Type</Label><Select value={newTransaction.type} onValueChange={function(v){setNewTransaction(function(p){return{...p,type:v};});}}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="expense">Expense</SelectItem><SelectItem value="income">Income</SelectItem></SelectContent></Select></div>
              <div><Label>Category</Label><Select value={newTransaction.category} onValueChange={function(v){setNewTransaction(function(p){return{...p,category:v};});}}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(function(c){return <SelectItem key={c} value={c}>{c}</SelectItem>;})}</SelectContent></Select></div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
            <button className="btn btn-g" onClick={function(){setShowAddModal(false);}}>Cancel</button>
            <button className="btn btn-p" onClick={handleAddTransaction} disabled={adding}>{adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
